import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatSeconds } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { BookOpen, CalendarDays, ChevronLeft, ChevronRight, Crown, Flame, Lock, Star, Timer as TimerIcon, Trophy } from "lucide-react";
import { useAccess } from "../context/AccessContext";

/**
 * Study Calendar — GitHub-style contribution heatmap over the user's own
 * `study_sessions`. Read-only. Uses the new `/api/study-calendar?month=YYYY-MM`
 * endpoint which aggregates per-day totals from the existing collection.
 *
 * Performance model:
 *  • Fetches ONE month at a time (~2 KB payload).
 *  • Per-month responses cached in a module-scoped Map; navigating back to
 *    a previously-viewed month is zero-network.
 *  • Streak stats + subject dictionary come back on every call but are cheap
 *    (indexed aggregations); the client only re-renders when they actually
 *    change.
 *  • Grid rendered via a memoised structure — no expensive work per render.
 *  • Zero polling, zero WebSocket, zero background timer. Static after load.
 *
 * UI: matches Syllox premium design language — Card containers, emerald
 * accents, calm empty states, dialog for day details.
 */

// ─── Intensity mapping (GitHub-style, Syllox emerald palette) ────────────
// Buckets in MINUTES of study for the day:
//   0        → level 0 (empty tile)
//   1–30 min → level 1 (very light)
//   30–60    → level 2 (light)
//   1–2 h    → level 3 (medium)
//   2–4 h    → level 4 (dark)
//   4+ h     → level 5 (bright neon — the "flex")
function bucketFor(totalSeconds) {
  const m = (totalSeconds || 0) / 60;
  if (m <= 0) return 0;
  if (m < 30) return 1;
  if (m < 60) return 2;
  if (m < 120) return 3;
  if (m < 240) return 4;
  return 5;
}

// Tailwind classes per bucket. We rely on Tailwind's dark: variant to keep
// the "empty" tile subtle on both themes.
const BUCKET_CLASSES = {
  0: "bg-zinc-100 dark:bg-zinc-800/60",
  1: "bg-emerald-100 dark:bg-emerald-900/50",
  2: "bg-emerald-300 dark:bg-emerald-700/70",
  3: "bg-emerald-400 dark:bg-emerald-600",
  4: "bg-emerald-500 dark:bg-emerald-500",
  5: "bg-emerald-400 shadow-[0_0_8px_1px_rgba(16,185,129,0.7)] dark:bg-emerald-400 dark:shadow-[0_0_10px_2px_rgba(52,211,153,0.9)]",
};

// ─── Utilities ────────────────────────────────────────────────────────────
function toMonthKey(year, monthIdx0) {
  // monthIdx0 is 0-based like JS Date. Backend expects `YYYY-MM`.
  const mm = String(monthIdx0 + 1).padStart(2, "0");
  return `${year}-${mm}`;
}

function parseMonthKey(key) {
  const [y, m] = key.split("-").map(Number);
  return { year: y, monthIdx0: m - 1 };
}

function dateStr(year, monthIdx0, day) {
  return `${year}-${String(monthIdx0 + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Format YYYY-MM into "February 2026"
function monthLabel(key) {
  const { year, monthIdx0 } = parseMonthKey(key);
  const d = new Date(year, monthIdx0, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// Weekday header (Sun-first, matching the calendar grid below)
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

// ─── Module-scope cache ───────────────────────────────────────────────────
// Persists across navigation but not across full reloads. Keyed by month.
// Cache size is naturally tiny (12 months × ~2 KB = 24 KB even for a
// power user who scrolls all year), so no eviction is necessary.
const _monthCache = new Map();

// Convert account_created_at into a { year, monthIdx0 } floor
function firstAllowedMonth(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), monthIdx0: d.getMonth() };
}

// Current (viewer's local) month key.
function currentMonthKey() {
  const d = new Date();
  return toMonthKey(d.getFullYear(), d.getMonth());
}

// ─── Weekly Recap ─────────────────────────────────────────────────────────
// Derives all metrics ENTIRELY from the current month's `days` array (already
// fetched for the heatmap). Zero extra API calls. If today's week extends
// back into last month, those days show as zero — a small trade-off for the
// "no additional network" performance guarantee.
//
// Week convention: Sunday → today (partial week ending today). Uses LOCAL
// calendar dates (browser tz — for our Indian audience this equals IST) so
// the boundary matches the backend's IST-midnight day rollover exactly. We
// deliberately avoid `toISOString().slice(0,10)` here — that returns UTC
// and would misclassify sessions between 00:00–05:29 IST as "yesterday",
// producing mismatches with Dashboard/Streak/Calendar.
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function computeWeeklyRecap(days, subjectsMap, currentStreak) {
  const today = new Date();
  const dow = today.getDay(); // 0 = Sun
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - dow);
  weekStart.setHours(0, 0, 0, 0);
  const weekStartStr = localDateStr(weekStart);
  const todayStr = localDateStr(today);

  const inWeek = (days || []).filter((d) => d.date >= weekStartStr && d.date <= todayStr);

  let totalSeconds = 0;
  let sessions = 0;
  const subjectIds = new Set();
  let bestDay = null; // {date, total_seconds}
  for (const d of inWeek) {
    totalSeconds += d.total_seconds || 0;
    sessions += d.session_count || 0;
    for (const sid of d.subject_ids || []) subjectIds.add(sid);
    if (!bestDay || (d.total_seconds || 0) > (bestDay.total_seconds || 0)) {
      bestDay = d;
    }
  }
  const subjectNames = Array.from(subjectIds)
    .map((id) => subjectsMap?.[id])
    .filter(Boolean);

  // Motivational message tiers — cheap, deterministic, single string.
  let motivation;
  if (totalSeconds === 0) {
    motivation = "Every legend started with the first minute. Open the Timer and go.";
  } else if (totalSeconds >= 60 * 60 * 20) {
    motivation = "20+ hours this week — you're on absolute fire. Keep the momentum. 🔥";
  } else if (totalSeconds >= 60 * 60 * 10) {
    motivation = "Double-digit hours this week. That's serious consistency. 💪";
  } else if (currentStreak >= 3) {
    motivation = `${currentStreak}-day streak · you're building the habit that wins. Keep going!`;
  } else {
    motivation = "Keep going — you're building consistency, one session at a time.";
  }

  return {
    totalSeconds,
    sessions,
    subjectCount: subjectNames.length,
    subjectNames,
    bestDay,
    weekStartStr,
    motivation,
  };
}

function formatBestDayLabel(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// ─── Component ────────────────────────────────────────────────────────────
export default function Calendar() {
  const { effectivePremium } = useAccess();
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // Guard against a race where a slow response overwrites a newer month's data
  const requestSeqRef = useRef(0);

  const fetchMonth = useCallback(async (key) => {
    // Premium-only endpoint. Skip fetching entirely for free users — the
    // lock screen below handles the empty-state UX without a wasted 403.
    if (!effectivePremium) return;
    if (_monthCache.has(key)) {
      setData(_monthCache.get(key));
      return;
    }
    const mySeq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const { data: payload } = await api.get("/study-calendar", { params: { month: key } });
      if (mySeq !== requestSeqRef.current) return; // A newer request is in-flight — bail.
      _monthCache.set(key, payload);
      setData(payload);
    } catch (e) {
      if (mySeq === requestSeqRef.current) {
        // Soft failure: render the month grid empty. Never blocks the page.
        setData({
          month: key,
          days_in_month: 30,
          days: [],
          subjects: {},
          streaks: { current: 0, longest: 0 },
          account_created_at: null,
          _error: true,
        });
      }
    } finally {
      if (mySeq === requestSeqRef.current) setLoading(false);
    }
  }, [effectivePremium]);

  useEffect(() => { fetchMonth(monthKey); }, [monthKey, fetchMonth]);

  // ─── Derived state (memoised — no work per keystroke / hover) ──────────
  const dayIndex = useMemo(() => {
    const map = new Map();
    for (const d of data?.days || []) map.set(d.date, d);
    return map;
  }, [data]);

  const { year, monthIdx0 } = useMemo(() => parseMonthKey(monthKey), [monthKey]);
  const daysInMonth = data?.days_in_month || 31;

  // First-of-month weekday (0 = Sun) for the grid offset
  const firstWeekday = useMemo(
    () => new Date(year, monthIdx0, 1).getDay(),
    [year, monthIdx0],
  );

  // Cell array: leading blanks + all days of the month. Simple flat array
  // is fine — modern React handles 42 nodes without breaking a sweat.
  const cells = useMemo(() => {
    const out = [];
    for (let i = 0; i < firstWeekday; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [firstWeekday, daysInMonth]);

  // Navigation guards
  const canGoNext = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    if (year < currentYear) return true;
    if (year === currentYear && monthIdx0 < currentMonth) return true;
    return false;
  }, [year, monthIdx0]);

  const canGoPrev = useMemo(() => {
    const floor = firstAllowedMonth(data?.account_created_at);
    if (!floor) return true;
    if (year > floor.year) return true;
    if (year === floor.year && monthIdx0 > floor.monthIdx0) return true;
    return false;
  }, [year, monthIdx0, data?.account_created_at]);

  const goPrev = () => {
    if (!canGoPrev) return;
    const d = new Date(year, monthIdx0 - 1, 1);
    setMonthKey(toMonthKey(d.getFullYear(), d.getMonth()));
  };
  const goNext = () => {
    if (!canGoNext) return;
    const d = new Date(year, monthIdx0 + 1, 1);
    setMonthKey(toMonthKey(d.getFullYear(), d.getMonth()));
  };

  // Details for the modal
  const selectedInfo = useMemo(() => {
    if (!selectedDate) return null;
    const entry = dayIndex.get(selectedDate);
    if (!entry) return { date: selectedDate, total_seconds: 0, session_count: 0, subjects: [] };
    const subjectMap = data?.subjects || {};
    const subjectNames = (entry.subject_ids || [])
      .map((id) => subjectMap[id] || null)
      .filter(Boolean);
    return {
      date: selectedDate,
      total_seconds: entry.total_seconds,
      session_count: entry.session_count,
      subjects: subjectNames,
    };
  }, [selectedDate, dayIndex, data?.subjects]);

  const streaks = data?.streaks || { current: 0, longest: 0 };

  // Weekly Recap — pure client-side derivation from `data.days`. Zero extra
  // network. Memoised so it only recomputes when the underlying month payload
  // changes.
  const weeklyRecap = useMemo(
    () => computeWeeklyRecap(data?.days, data?.subjects, streaks.current),
    [data?.days, data?.subjects, streaks.current],
  );

  // ─── Premium lock screen ─────────────────────────────────────────────
  // Rendered INSIDE the same Layout (sidebar visible per spec) but replaces
  // the calendar content with a friendly upgrade CTA. Reuses the existing
  // AccessContext so trial + Founder's Reward users pass through
  // automatically — no duplicate permission system. Placed AFTER all hooks
  // above to keep hook call order stable across renders (React rules).
  if (!effectivePremium) {
    return (
      <div className="space-y-6" data-testid="calendar-root">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Momentum</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Study Calendar</h1>
          <p className="text-zinc-600 dark:text-zinc-400">
            Every studied day is a green tile. Deeper green means you went longer.
          </p>
        </div>

        <div
          className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 via-white to-white p-8 text-center dark:border-emerald-500/25 dark:from-emerald-500/10 dark:via-zinc-900 dark:to-zinc-900"
          data-testid="calendar-locked"
        >
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25">
            <Lock size={26} />
          </div>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
            <Crown size={11} /> Premium feature
          </div>
          <h2 className="mt-3 font-heading text-2xl font-semibold sm:text-3xl">
            Unlock your Study Calendar
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-zinc-600 dark:text-zinc-400">
            See a GitHub-style heatmap of every studied day, deeper green for
            longer sessions. Track your current & longest streaks, review daily
            breakdowns with subjects and totals, and never lose sight of your
            momentum. Included with Syllox Premium — plus the free{" "}
            <span className="font-semibold text-emerald-600 dark:text-emerald-400">30-Day Premium Trial</span>.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { icon: <CalendarDays size={16} />, label: "Monthly heatmap" },
              { icon: <Flame size={16} className="text-orange-500" />, label: "Streak stats" },
              { icon: <BookOpen size={16} />, label: "Per-day details" },
              { icon: <Star size={16} className="text-amber-500" />, label: "Best day of the week" },
              { icon: <TimerIcon size={16} />, label: "Weekly recap" },
              { icon: <Trophy size={16} className="text-amber-500" />, label: "Consistency proof" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white/60 px-3 py-2 text-xs font-medium text-zinc-700 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300">
                {f.icon} <span>{f.label}</span>
              </div>
            ))}
          </div>
          <Link to="/premium" className="mt-6 inline-block" data-testid="calendar-upgrade-cta">
            <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-500/25 hover:from-emerald-600 hover:to-emerald-700">
              <Crown size={14} className="mr-1.5" /> Upgrade to Premium
            </Button>
          </Link>
          <p className="mt-3 text-xs font-medium text-emerald-700 dark:text-emerald-400" data-testid="calendar-locked-pricing">
            ₹49/month · ₹299/year
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-root">
      {/* Page header */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Momentum</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Study Calendar</h1>
        <p className="text-zinc-600 dark:text-zinc-400">
          Every studied day is a green tile. Deeper green means you went longer.
        </p>
      </div>

      {/* Weekly Recap card — derived from the current month's aggregation.
          Zero extra API calls. Hidden until the month payload arrives so we
          never flash zeros. */}
      {data && (
        <div
          className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 via-white to-white p-5 dark:border-emerald-500/25 dark:from-emerald-500/10 dark:via-zinc-900 dark:to-zinc-900"
          data-testid="calendar-weekly-recap"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600">This week</div>
              <h2 className="mt-0.5 font-heading text-xl font-semibold">Your weekly recap</h2>
            </div>
            <div className="text-xs text-zinc-500">
              Since {new Date(weeklyRecap.weekStartStr).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {/* Total study time */}
            <div className="rounded-xl border border-zinc-200 bg-white/70 p-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/70" data-testid="recap-total-time">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <BookOpen size={12} className="text-emerald-500" /> Total time
              </div>
              <div className="mt-1 font-heading text-lg font-bold tabular-nums">
                {formatSeconds(weeklyRecap.totalSeconds)}
              </div>
            </div>
            {/* Current streak (same source as the streak card below — kept here
                for at-a-glance recap density; single number, cheap to render). */}
            <div className="rounded-xl border border-zinc-200 bg-white/70 p-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/70" data-testid="recap-current-streak">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <Flame size={12} className="text-orange-500" /> Streak
              </div>
              <div className="mt-1 font-heading text-lg font-bold tabular-nums">
                {streaks.current}
                <span className="ml-1 text-xs font-medium text-zinc-500">
                  {streaks.current === 1 ? "day" : "days"}
                </span>
              </div>
            </div>
            {/* Subjects touched this week */}
            <div className="rounded-xl border border-zinc-200 bg-white/70 p-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/70" data-testid="recap-subjects">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <BookOpen size={12} className="text-blue-500" /> Subjects
              </div>
              <div className="mt-1 font-heading text-lg font-bold tabular-nums">
                {weeklyRecap.subjectCount}
              </div>
              {weeklyRecap.subjectNames.length > 0 && (
                <div className="mt-0.5 truncate text-[10px] text-zinc-500" title={weeklyRecap.subjectNames.join(", ")}>
                  {weeklyRecap.subjectNames.slice(0, 3).join(" · ")}
                  {weeklyRecap.subjectNames.length > 3 && ` +${weeklyRecap.subjectNames.length - 3}`}
                </div>
              )}
            </div>
            {/* Total sessions */}
            <div className="rounded-xl border border-zinc-200 bg-white/70 p-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/70" data-testid="recap-sessions">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <TimerIcon size={12} className="text-purple-500" /> Sessions
              </div>
              <div className="mt-1 font-heading text-lg font-bold tabular-nums">
                {weeklyRecap.sessions}
              </div>
            </div>
            {/* Best day */}
            <div className="col-span-2 rounded-xl border border-zinc-200 bg-white/70 p-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/70 sm:col-span-1 lg:col-span-1" data-testid="recap-best-day">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <Star size={12} className="text-amber-500" /> Best day
              </div>
              <div className="mt-1 font-heading text-sm font-bold">
                {weeklyRecap.bestDay ? formatBestDayLabel(weeklyRecap.bestDay.date) : "—"}
              </div>
              {weeklyRecap.bestDay && (
                <div className="text-[10px] text-emerald-600 dark:text-emerald-400">
                  {formatSeconds(weeklyRecap.bestDay.total_seconds)}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 text-sm text-zinc-600 dark:text-zinc-400" data-testid="recap-motivation">
            {weeklyRecap.motivation}
          </div>
        </div>
      )}

      {/* Streak cards */}
      <div className="grid grid-cols-2 gap-4" data-testid="calendar-streak-cards">
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Flame size={14} className="text-orange-500" /> Current streak
          </div>
          <div className="mt-1 font-heading text-3xl font-bold text-zinc-900 dark:text-white" data-testid="calendar-current-streak">
            {streaks.current}
            <span className="ml-2 text-base font-medium text-zinc-500">
              {streaks.current === 1 ? "day" : "days"}
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Trophy size={14} className="text-amber-500" /> Longest streak
          </div>
          <div className="mt-1 font-heading text-3xl font-bold text-zinc-900 dark:text-white" data-testid="calendar-longest-streak">
            {streaks.longest}
            <span className="ml-2 text-base font-medium text-zinc-500">
              {streaks.longest === 1 ? "day" : "days"}
            </span>
          </div>
        </div>
      </div>

      {/* Calendar card */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="calendar-card">
        <div className="flex items-center justify-between">
          <button
            onClick={goPrev}
            disabled={!canGoPrev}
            className="grid h-9 w-9 place-items-center rounded-lg border border-zinc-200 text-zinc-600 transition hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-40 disabled:hover:border-zinc-200 disabled:hover:text-zinc-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-emerald-500/60 dark:hover:text-emerald-400"
            aria-label="Previous month"
            data-testid="calendar-prev-month"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <CalendarDays size={16} className="text-emerald-600 dark:text-emerald-400" />
            <div className="font-heading text-lg font-semibold" data-testid="calendar-month-label">
              {monthLabel(monthKey)}
            </div>
          </div>
          <button
            onClick={goNext}
            disabled={!canGoNext}
            className="grid h-9 w-9 place-items-center rounded-lg border border-zinc-200 text-zinc-600 transition hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-40 disabled:hover:border-zinc-200 disabled:hover:text-zinc-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-emerald-500/60 dark:hover:text-emerald-400"
            aria-label="Next month"
            data-testid="calendar-next-month"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="mt-5 grid grid-cols-7 gap-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
          {WEEKDAYS.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-2 grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
          {cells.map((day, idx) => {
            if (day === null) return <div key={`b-${idx}`} className="aspect-square" />;
            const ds = dateStr(year, monthIdx0, day);
            const entry = dayIndex.get(ds);
            const bucket = entry ? bucketFor(entry.total_seconds) : 0;
            const hasData = bucket > 0;
            return (
              <button
                key={ds}
                type="button"
                onClick={() => setSelectedDate(ds)}
                title={hasData ? `${monthLabel(monthKey).split(" ")[0]} ${day} · ${formatSeconds(entry.total_seconds)}` : `${monthLabel(monthKey).split(" ")[0]} ${day} · No study`}
                data-testid={`calendar-cell-${ds}`}
                data-bucket={bucket}
                className={`group relative aspect-square rounded-md ${BUCKET_CLASSES[bucket]} transition-transform duration-150 hover:scale-105 hover:ring-2 hover:ring-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed`}
              >
                <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-semibold ${bucket >= 3 ? "text-emerald-950" : bucket >= 1 ? "text-emerald-800 dark:text-emerald-100" : "text-zinc-500 dark:text-zinc-500"}`}>
                  {day}
                </span>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
          <span>Less</span>
          {[0, 1, 2, 3, 4, 5].map((b) => (
            <span key={b} className={`h-3 w-3 rounded-sm ${BUCKET_CLASSES[b]}`} title={
              b === 0 ? "No study" :
              b === 1 ? "1–30 min" :
              b === 2 ? "30–60 min" :
              b === 3 ? "1–2 h" :
              b === 4 ? "2–4 h" : "4+ h"
            }/>
          ))}
          <span>More</span>
        </div>

        {loading && (
          <div className="mt-3 text-center text-xs text-zinc-500" data-testid="calendar-loading">Loading…</div>
        )}
      </div>

      {/* Day details modal */}
      <Dialog open={!!selectedDate} onOpenChange={(v) => !v && setSelectedDate(null)}>
        <DialogContent className="sm:max-w-md" data-testid="calendar-day-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedInfo && new Date(selectedInfo.date).toLocaleDateString(undefined, {
                weekday: "long", month: "long", day: "numeric", year: "numeric",
              })}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedInfo?.total_seconds > 0
                ? "Your study on this day."
                : "No study logged on this day."}
            </DialogDescription>
          </DialogHeader>

          {selectedInfo && selectedInfo.total_seconds > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-800">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Study time</div>
                  <div className="mt-1 font-heading text-xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="calendar-day-total">
                    {formatSeconds(selectedInfo.total_seconds)}
                  </div>
                </div>
                <div className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-800">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Sessions</div>
                  <div className="mt-1 font-heading text-xl font-bold" data-testid="calendar-day-sessions">
                    {selectedInfo.session_count}
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-800">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Subjects</div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="calendar-day-subjects">
                  {selectedInfo.subjects.length === 0 ? (
                    <span className="text-sm text-zinc-500">— (no subject tagged)</span>
                  ) : (
                    selectedInfo.subjects.map((n) => (
                      <span key={n} className="inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        {n}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-500 dark:border-zinc-800">
              Take a break today, or start the timer to make it count.
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <Button variant="outline" onClick={() => setSelectedDate(null)} data-testid="calendar-day-close">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
